import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import ActionButton from 'components/common/ActionButton';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { numberFormatter } from 'helpers/utils';
import {
  toggleSelectLandParcel,
  setLocation,
  removeDrawnLandParcel
} from 'reducers/createListingReducer';

const ParcelTable = ({ errors, setError, clearErrors }) => {
  const dispatch = useDispatch();

  const [data, setData] = useState([]);

  const { selectedLandParcels } = useSelector(state => state.createListing);

  const handleLandParcelCenter = id => {
    const p = selectedLandParcels.find(p => p.id === id);
    const sumLng = p.coordinates.reduce((acc, value) => acc + value[0], 0);
    const sumLat = p.coordinates.reduce((acc, value) => acc + value[1], 0);
    const location = {
      lat: sumLat / p.coordinates.length,
      lng: sumLng / p.coordinates.length
    };
    dispatch(setLocation(location));
  };

  const handleRemoveLandParcel = id => {
    const p = selectedLandParcels.find(p => p.id === id);
    if (p.isDrawn) {
      dispatch(removeDrawnLandParcel({ id }));
    } else {
      dispatch(toggleSelectLandParcel({ id }));
    }
  };

  const columns = [
    {
      accessorKey: 'number',
      header: 'Parcel'
    },
    {
      accessorKey: 'area',
      header: 'Est. Area (ha)',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: ({ row: { original } }) => {
        const { area } = original;
        return numberFormatter(area, 2);
      }
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row: { original } }) => {
        const { id } = original;
        return (
          <>
            <ActionButton
              icon="arrows-to-dot"
              title="Center"
              variant="action"
              className="p-0 me-2"
              onClick={() => handleLandParcelCenter(id)}
            />
            <ActionButton
              icon="trash-alt"
              title="Remove"
              variant="action"
              className="p-0"
              onClick={() => handleRemoveLandParcel(id)}
            />
          </>
        );
      }
    }
  ];

  useEffect(() => {
    setData(
      selectedLandParcels.map((p, i) => ({
        id: p.id,
        number: i + 1,
        area: p.area
      }))
    );
    if (selectedLandParcels.length === 0) {
      setError('selectedLandParcels', {
        type: 'custom',
        message: 'You must select at least one land parcel'
      });
    } else {
      clearErrors('selectedLandParcels');
    }
  }, [selectedLandParcels]);

  const table = useAdvanceTable({
    data,
    columns,
    sortable: true,
    pagination: true,
    perPage: 10
  });

  return (
    <AdvanceTableProvider {...table}>
      <AdvanceTable
        headerClassName="bg-200 text-nowrap align-middle"
        rowClassName="align-middle white-space-nowrap"
        tableProps={{
          bordered: true,
          striped: true,
          className: 'fs-10 mb-0 overflow-hidden'
        }}
        emptyMessage="No land parcels selected"
      />
      <div className="mt-3">
        <AdvanceTableFooter rowInfo navButtons />
      </div>
      <Form.Control
        type="hidden"
        isInvalid={errors.selectedLandParcels}
      ></Form.Control>
      <Form.Control.Feedback type="invalid">
        {errors.selectedLandParcels?.message}
      </Form.Control.Feedback>
    </AdvanceTableProvider>
  );
};

ParcelTable.propTypes = {
  errors: PropTypes.object.isRequired,
  setError: PropTypes.func.isRequired,
  clearErrors: PropTypes.func.isRequired
};

export default ParcelTable;
